import Vue from 'vue';
import pageContentModule from '../store/pageModules';
import pageNotFoundModule from '../store/pageNotFound';
import { eventManager } from '@breakmedia/prime-vue-event-manager';

const page = {
    async loadPage(
        to,
        layoutLoaded = false,
        templateLoaded = false,
        deferredCommit = false,
        currentNavigationId = null,
    ) {
        if (false == shouldContinueNavigation(currentNavigationId)) {
            return Promise.reject({});
        }
        if (!layoutLoaded || !templateLoaded) {
            return this.fetchPage(to)
                .then(async page => {
                    if (deferredCommit) {
                        return () => this.setPage(page, layoutLoaded, templateLoaded);
                    } else {
                        return this.setPage(page, layoutLoaded, templateLoaded);
                    }
                })
                .catch(e => {
                    Vue.$store.commit('PageContent/setCurrentPage', {});
                    return {};
                });
        }
    },
    async fetchPage(to, setPage = false) {
        const promises = [];
        promises.push(Vue.$language.waitLanguage());
        promises.push(Vue.$country.waitCountry());
        const [language, country] = await Promise.all(promises);
        return await Vue.$store
            .dispatch('PageContent/loadPage', {
                path: to.meta.pageUrl ? to.meta.pageUrl : to.path,
                resource: to.meta.resource,
                language,
                country,
            })
            .then(page => {
                if (setPage === true) {
                    Vue.$store.commit('PageContent/setCurrentPage', page);
                }
                return page;
            });
    },
    async setPage(page, layoutLoaded, templateLoaded) {
        if (!page || !Object.keys(page).length) {
            await Vue.$store.dispatch('PageNotFound/updateVisibility', true);
            await Vue.$store.commit('PageContent/setCurrentTemplate', null);
        } else {
            await Vue.$store.dispatch('PageNotFound/updateVisibility', false);
            if (!templateLoaded) {
                await Vue.$store.dispatch('PageContent/switchTemplate', page.template_file);
            }

            Vue.$store.commit('PageContent/setCurrentPage', page);

            Vue.$store.commit('PageContent/update', {
                params: {
                    pageId: page.id,
                },
                result: page.page_modules,
            });

            if (!layoutLoaded) {
                await Vue.$store.dispatch('Layout/switchLayout', {
                    layout: page.layout_file,
                });
            }
        }
    },
};

let navigationId = null;

export default {
    install(Vue) {
        if (!Vue.$store.state['PageContent']) {
            Vue.$store.registerModule('PageContent', pageContentModule);
        }
        if (!Vue.$store.state['PageNotFound']) {
            Vue.$store.registerModule('PageNotFound', pageNotFoundModule);
        }

        Vue.$router.beforeEach(async function(to, from, next) {
            if (!from.name || to.path !== from.path) {
                Vue.$store.commit('setDisplayProgressBar', true);
                if (Vue.$store.state.PageContent.prerenderMode === undefined) {
                    let prerenderMode = false;
                    if (to.query && to.query.prerenderMode) {
                        prerenderMode = true;
                    }
                    Vue.$store.commit('PageContent/setPrerenderMode', prerenderMode);
                }
                if (!Vue.$store.state.PageContent.page) {
                    page.fetchPage(to, true)
                        .then(() => {
                            switchPage(to, next, from);
                        })
                        .catch(() => {
                            Vue.$store.commit('PageContent/setCurrentTemplate', null);
                            Vue.$store.dispatch('PageNotFound/updateVisibility', true);
                            switchPage(to, next, from);
                        });
                } else {
                    navigationId = Date.now();
                    switchPage(to, next, from, navigationId);
                }
            } else {
                next();
            }
        });

        Vue.$router.afterEach(function(to) {
            eventManager.$emit('pageView', {
                path: to.path,
                eventId: eventManager.getUniqueId('pageView'),
            });
        });

        Vue.$store.watch(
            () => Vue.$language.getLanguage(),
            (value, oldValue) => {
                if (oldValue !== null) {
                    switchPage(Vue.$router.currentRoute);
                }
            },
        );

        Vue.$store.watch(
            () => Vue.$country.getCountryID(),
            (value, oldValue) => {
                if (oldValue !== null) {
                    switchPage(Vue.$router.currentRoute);
                }
            },
        );
    },
};

let currentStartTime = null;

const switchPage = (to, next = null, from = null, currentNavigationId = null) => {
    const startTime = Date.now();
    currentStartTime = startTime;

    if (to.matched.length && to.matched[0] && to.matched[0].components.default) {
        if (typeof to.matched[0].components.default === 'function') {
            Vue.$prepareComponentInstance(to.matched[0].components.default).then(component => {
                if (typeof component.getOwnLayout === 'function') {
                    executeGetOwnLayout(component, to, from, next, startTime, currentNavigationId);
                } else {
                    page.loadPage(to, false, false, false, currentNavigationId)
                        .then(() => {
                            onNext(next);
                        })
                        .catch(() => {});
                }
            });
        } else if (typeof to.matched[0].components.default.getOwnLayout === 'function') {
            executeGetOwnLayout(to.matched[0].components.default, to, from, next, startTime, currentNavigationId);
        } else {
            page.loadPage(to, false, false, true, currentNavigationId)
                .then(callback => {
                    typeof next === 'function' ? onNext(next) : null;
                    typeof callback === 'function' ? callback() : null;
                })
                .catch(() => {});
        }
    } else {
        page.loadPage(to, false, false, false, currentNavigationId)
            .then(() => {
                onNext(next);
            })
            .catch(() => {});
    }
};

const process = (result, component, to, next, currentNavigationId = null) => {
    if (false == shouldContinueNavigation(currentNavigationId)) {
        return;
    }
    if (result.layout) {
        Vue.$store
            .dispatch('Layout/switchLayout', {
                layout: {
                    name: result.layout,
                    path: '/layout/',
                },
            })
            .then(() => {
                if (result.template) {
                    Vue.$store
                        .dispatch('PageContent/switchTemplate', {
                            name: result.template,
                            path: '/template/',
                            fallback: result.fallback,
                        })
                        .then(() => afterLoadPage(component, to, next));
                } else {
                    page.loadPage(to, true)
                        .then(() => afterLoadPage(component, to, next))
                        .catch(() => {});
                }
            });
    } else {
        if (result.template) {
            Vue.$store
                .dispatch('PageContent/switchTemplate', {
                    name: result.template,
                    path: '/template/',
                    fallback: result.fallback,
                })
                .then(() =>
                    page
                        .loadPage(to, false, true)
                        .then(() => afterLoadPage(component, to, next))
                        .catch(() => {}),
                );
        } else {
            page.loadPage(to)
                .then(() => afterLoadPage(component, to, next))
                .catch(() => {});
        }
    }
};

const afterLoadPage = (component, to, next) => {
    if (typeof component.onLayoutReady == 'function') {
        component.onLayoutReady(to);
    }
    onNext(next);
};

const executeGetOwnLayout = (component, to, from, next, startTime, currentNavigationId = null) => {
    component
        .getOwnLayout(to, from)
        .then(result => {
            if (false == shouldContinueNavigation(currentNavigationId)) {
                return;
            }
            if (result.hasOwnProperty('freezeLayoutSwitch')) {
                Vue.$store
                    .dispatch('PageContent/switchTemplate', {
                        name: result.template,
                        path: '/template/',
                    })
                    .then(() => {
                        page.loadPage(to, true, true, false, currentNavigationId)
                            .then(() => afterLoadPage(component, to, next))
                            .catch(() => {});

                        result.freezeLayoutSwitch.then(() => {
                            if (currentStartTime === startTime) {
                                process(result, component, to, next, currentNavigationId);
                            }
                        });
                    });
            } else {
                process(result, component, to, next, currentNavigationId);
            }
        })
        .catch(() =>
            page
                .loadPage(to, false, false, false, currentNavigationId)
                .then(() => afterLoadPage(component, to, next))
                .catch(() => {}),
        );
};

const onNext = (next = null) => {
    Vue.$store.commit('setDisplayProgressBar', false);
    if (next) {
        navigationId = null;
        next();
    }
};

const shouldContinueNavigation = currentNavigationId => {
    let result = true;
    if (currentNavigationId) {
        result = navigationId == currentNavigationId;
    }
    return result;
};
